import { useTranslation } from "react-i18next";
import { Mic, MicOff, Video, VideoOff, PhoneOff } from "lucide-react";
import { Button, cn } from "@tellescope/ui";
import { useVideoCalling } from "../hooks/useVideoCallingContext";
import { useLocalTracks } from "../hooks/useLocalTracks";
import type { VideoControlsProps } from "../types";

/**
 * VideoControls - Control bar for video calls.
 *
 * Provides audio mute, video mute, leave, and end-for-all buttons.
 */
export function VideoControls({ userRole, showEndForAll = false, onLeave, onEndForAll }: VideoControlsProps) {
  const { t } = useTranslation("videoCalling");
  const videoCalling = useVideoCalling();
  const { toggleVideo, toggleAudio } = useLocalTracks();

  const { isVideoEnabled, isAudioEnabled } = videoCalling;

  return (
    <div className="flex justify-center items-center gap-2 p-3 bg-gray-900 border-t border-gray-800">
      {/* Audio toggle */}
      <Button
        variant="outline"
        size="icon"
        onClick={toggleAudio}
        className={cn(
          "rounded-full h-12 w-12 !bg-gray-800 !border-gray-600 !text-white hover:!bg-gray-700",
          !isAudioEnabled && "!bg-red-500/20 !text-red-400 !border-red-400/50 hover:!bg-red-500/30"
        )}
        title={isAudioEnabled ? t("Mute") : t("Unmute")}
      >
        {isAudioEnabled ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
      </Button>

      {/* Video toggle */}
      <Button
        variant="outline"
        size="icon"
        onClick={toggleVideo}
        className={cn(
          "rounded-full h-12 w-12 !bg-gray-800 !border-gray-600 !text-white hover:!bg-gray-700",
          !isVideoEnabled && "!bg-red-500/20 !text-red-400 !border-red-400/50 hover:!bg-red-500/30"
        )}
        title={isVideoEnabled ? t("Turn off camera") : t("Turn on camera")}
      >
        {isVideoEnabled ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
      </Button>

      {/* Leave call */}
      <Button variant="destructive" onClick={onLeave} className="rounded-full h-12 px-6">
        <PhoneOff className="h-5 w-5 mr-2" />
        {t("Leave")}
      </Button>

      {/* End for all - host only */}
      {userRole === "host" && showEndForAll && onEndForAll && (
        <Button
          variant="outline"
          onClick={onEndForAll}
          className="rounded-full h-12 px-4 text-red-400 border-red-400/50 hover:bg-red-500/10 hover:text-red-300"
        >
          {t("End for All")}
        </Button>
      )}
    </div>
  );
}
